import {
  type FlatButtonLinkProps,
  type PrimaryButtonLinkProps,
  type SecondaryButtonLinkProps,
  FlatButtonLink as WebUiFlatButtonLink,
  PrimaryButtonLink as WebUiPrimaryButtonLink,
  SecondaryButtonLink as WebUiSecondaryButtonLink,
} from '@stenajs-webui/elements';
import type { MouseEvent, ReactNode, Ref } from 'react';
import { type LinkProps as RRLinkProps, useHref, useLinkClickHandler } from 'react-router-dom';

type LinkRenderProps = {
  href: string;
  onClick: (event: MouseEvent<HTMLAnchorElement>) => void;
  ref?: Ref<HTMLAnchorElement>;
  target?: string;
} & Record<string, unknown>;

interface LinkProps extends Omit<RRLinkProps, 'onClick' | 'children'> {
  onClick?: ((event: MouseEvent<HTMLAnchorElement>, isModified: boolean) => void) | undefined;
  ref?: Ref<HTMLAnchorElement>;
  render: (props: LinkRenderProps) => ReactNode;
}

function isModifiedEvent<T>(event: MouseEvent<T>): boolean {
  return event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
}

// FIXME: Replace render prop API with react-router connected webui-links
//  and document how to do this in webui
export const Link = ({ to, target, state, render, onClick, replace, ...props }: LinkProps) => {
  const href = useHref(to);
  const handleClick = useLinkClickHandler(to, {
    target,
    state,
    replace,
  });

  const linkRenderProps: LinkRenderProps = {
    ...props,
    href,
    onClick: (event: MouseEvent<HTMLAnchorElement>) => {
      const isModified =
        event.defaultPrevented ||
        event.button !== 0 ||
        (target && target !== '_self') ||
        isModifiedEvent(event);

      onClick?.(event, isModified);
      if (!event.defaultPrevented) {
        handleClick(event);
      }
    },
    target,
  };

  return render(linkRenderProps);
};

export const FlatButtonLink = (
  outerProps: Omit<LinkProps, 'render'> & Omit<FlatButtonLinkProps, 'href'>,
) => (
  <Link
    {...outerProps}
    render={({ ref, ...props }) => <WebUiFlatButtonLink {...props} ref={ref} />}
  />
);

export const SecondaryButtonLink = (
  outerProps: Omit<LinkProps, 'render'> & Omit<SecondaryButtonLinkProps, 'href'>,
) => (
  <Link
    {...outerProps}
    render={({ ref, ...props }) => <WebUiSecondaryButtonLink {...props} ref={ref} />}
  />
);

export const PrimaryButtonLink = (
  outerProps: Omit<LinkProps, 'render'> & Omit<PrimaryButtonLinkProps, 'href'>,
) => (
  <Link
    {...outerProps}
    render={({ ref, ...props }) => <WebUiPrimaryButtonLink {...props} ref={ref} />}
  />
);
